import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GoogleLogin from './GoogleLogin';
import ForgotPasswordModal from './ForgotPasswordModal';
import { theme } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config';
import { useReferrals } from '../hooks/useReferrals';
import { track } from '../lib/analytics';
import { isAdminUser } from '../utils/roles';

const AuthPage: React.FC = () => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { setToken, setUser } = useAuth();

  // Check if user is coming from a match invite
  const joinData = (() => {
    try {
      const raw = localStorage.getItem('joinMatchAfterLogin');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const hasMatchContext = !!joinData?.matchLocation;

  const [mode, setMode] = useState<'login' | 'register'>(hasMatchContext ? 'register' : 'login');

  // Funnel: intención de registrarse (primera vez que se entra en modo register).
  const signupStartedRef = useRef(false);
  useEffect(() => {
    if (mode === 'register' && !signupStartedRef.current) {
      signupStartedRef.current = true;
      track('signup_started');
    }
  }, [mode]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const { trackReferral } = useReferrals();

  // Pre-populate referral code from URL ?ref=CODE or from localStorage pendingReferralCode.
  // Also persist for cold-start (user opens link → leaves → reopens app).
  useEffect(() => {
    try {
      const urlCode = new URLSearchParams(window.location.search).get('ref')?.trim();
      const stored = localStorage.getItem('pendingReferralCode');
      const code = urlCode || stored;
      if (code) {
        setReferralCodeInput(code);
        if (urlCode) {
          localStorage.setItem('pendingReferralCode', urlCode);
          setMode('register');
        }
      }
    } catch { /* ignore */ }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/register';
      const body = mode === 'login'
        ? { email, password }
        : { email, password, name };

      const response = await fetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error en autenticación');
      }

      // Guardar token y usuario
      localStorage.setItem('authToken', data.token);
      setToken(data.token);
      setUser(data.user);

      // Track referral si el usuario ingresó un código al registrarse
      if (mode === 'register' && referralCodeInput.trim()) {
        const result = await trackReferral(referralCodeInput.trim());
        if (result.success) {
          localStorage.removeItem('pendingReferralCode');
        }
        // Error silencioso (ej: código inválido o ya consumido) — no bloquea el login
      }

      // Si debe cambiar password, redirigir a cambio de password
      if (data.user.mustChangePassword) {
        navigate('/change-password');
        return;
      }

      // Claim a pending group member profile if the user came from "¿Sos vos?"
      const claimPendingId = new URLSearchParams(window.location.search).get('claim_pending');
      const claimGroupId = new URLSearchParams(window.location.search).get('claim_group');
      if (claimPendingId && claimGroupId) {
        try {
          await fetch(getApiUrl(`/api/groups/${claimGroupId}/pending-members/${claimPendingId}/claim`), {
            method: 'POST',
            headers: { Authorization: `Bearer ${data.token}`, 'Content-Type': 'application/json' },
          });
        } catch { /* non-blocking */ }
      }

      // Check if coming from match invite — redirect to join
      const pendingJoin = localStorage.getItem('joinMatchAfterLogin');
      if (pendingJoin) {
        try {
          const { matchId } = JSON.parse(pendingJoin);
          if (matchId) {
            navigate(`/join/${matchId}`);
            return;
          }
        } catch { /* fall through to default redirect */ }
      }

      // Redirigir según rol
      if (isAdminUser(data.user)) {
        navigate('/admin');
      } else if (data.user.role === 'VENUE_OWNER') {
        navigate('/owner/reservations');
      } else {
        navigate('/home');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    color: theme.colors.text,
    fontSize: theme.fonts.sizeBody,
    fontFamily: theme.fonts.body,
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '12px',
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px',
    backgroundColor: theme.colors.primary,
    color: '#000',
    border: 'none',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: 'bold',
    cursor: loading ? 'not-allowed' : 'pointer',
    fontFamily: theme.fonts.body,
    opacity: loading ? 0.7 : 1,
    transition: 'all 0.2s ease',
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '20px 16px' : theme.spacing.lg,
      backgroundColor: theme.colors.background,
    }}>
      <div style={{
        width: '100%',
        maxWidth: isMobile ? '100%' : '600px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        {/* Match invite context banner */}
        {hasMatchContext && (
          <div style={{
            width: '100%',
            padding: '14px 20px',
            borderRadius: '14px',
            backgroundColor: 'rgba(0, 230, 118, 0.12)',
            border: '1px solid rgba(0, 230, 118, 0.3)',
            marginBottom: '16px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '14px', color: '#B0BEC5', marginBottom: '4px' }}>
              Te invitaron a un partido
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>
              {joinData.matchLocation}
            </div>
            {joinData.matchDate && joinData.matchTime && (
              <div style={{ fontSize: '14px', color: '#00E676', marginTop: '2px' }}>
                {(() => {
                  try {
                    const [y, m, d] = joinData.matchDate.split('-').map(Number);
                    const dt = new Date(y, m - 1, d);
                    const day = dt.toLocaleDateString('es-AR', { weekday: 'long' });
                    return `${day.charAt(0).toUpperCase() + day.slice(1)} ${d} · ${joinData.matchTime}hs`;
                  } catch { return `${joinData.matchTime}hs`; }
                })()}
              </div>
            )}
            <div style={{ fontSize: '13px', color: '#78909C', marginTop: '6px' }}>
              Creá tu cuenta gratis para anotarte
            </div>
          </div>
        )}

        {/* Header - compact when coming from match invite */}
        <div style={{
          textAlign: 'center',
          marginBottom: hasMatchContext ? '12px' : (isMobile ? '18px' : theme.spacing.lg),
          color: theme.colors.white
        }}>
          <h1 style={{
            fontSize: hasMatchContext ? '1.2rem' : (isMobile ? '2.2rem' : '4rem'),
            margin: hasMatchContext ? '0 0 4px 0' : (isMobile ? '0 0 10px 0' : '0 0 16px 0'),
            fontWeight: 800,
            fontFamily: theme.fonts.heading,
            letterSpacing: theme.fonts.letterSpacingWide,
            textTransform: 'uppercase',
            color: '#FFFFFF',
            textShadow: '0 4px 8px rgba(0,0,0,0.3)'
          }}>
            {hasMatchContext ? '' : '⚽ '}Futbolero
          </h1>
          {!hasMatchContext && <p style={{
            fontSize: isMobile ? '1rem' : theme.fonts.sizeSubtitle,
            margin: 0,
            opacity: 0.9,
            fontFamily: theme.fonts.body,
            textShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }}>
            Organiza tus partidos de fútbol de forma fácil y divertida
          </p>}
        </div>

        {/* Form Container */}
        <div style={{
          backgroundColor: theme.colors.surface,
          borderRadius: theme.borderRadius.card,
          padding: hasMatchContext ? '20px 20px' : (isMobile ? '24px 20px' : theme.spacing.lg),
          boxShadow: theme.colors.shadow,
          border: hasMatchContext ? '1px solid rgba(255,255,255,0.1)' : `2px solid ${theme.colors.primary}`,
          backdropFilter: 'blur(10px)',
          marginBottom: isMobile ? '18px' : 0,
          width: '100%',
          maxWidth: isMobile ? '360px' : '500px'
        }}>
          {/* Google Login */}
          {hasMatchContext && (
            <h2 style={{
              textAlign: 'center', margin: '0 0 16px', fontSize: '18px', fontWeight: 700,
              color: theme.colors.white, fontFamily: theme.fonts.heading,
            }}>
              Creá tu cuenta en 10 segundos
            </h2>
          )}
          <GoogleLogin compact={hasMatchContext} />

          {/* Divider + email form */}
          <>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            margin: '20px 0',
            color: theme.colors.textSecondary,
          }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: theme.colors.border }} />
            <span style={{ padding: '0 16px', fontSize: theme.fonts.sizeSmall }}>o</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: theme.colors.border }} />
          </div>

          <form onSubmit={handleSubmit}>
            {mode === 'register' && (
              <input
                type="text"
                placeholder="Nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle}
                required
              />
            )}

            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              required
            />

            <input
              type="password"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              required
              minLength={6}
            />

            {mode === 'register' && (
              <input
                type="text"
                placeholder="Código de referido (opcional)"
                value={referralCodeInput}
                onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase().slice(0, 12))}
                autoCapitalize="characters"
                style={{
                  ...inputStyle,
                  letterSpacing: '1px',
                  fontFamily: 'monospace',
                }}
                maxLength={12}
              />
            )}

            {error && (
              <div style={{
                color: theme.colors.danger,
                fontSize: theme.fonts.sizeSmall,
                marginBottom: '12px',
                textAlign: 'center',
              }}>
                {error}
              </div>
            )}

            <button type="submit" style={buttonStyle} disabled={loading}>
              {loading ? 'Cargando...' : mode === 'login' ? 'Iniciar Sesión' : 'Registrarse'}
            </button>
          </form>

          {/* Forgot password link (only in login mode) */}
          {mode === 'login' && (
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button
                type="button"
                onClick={() => setShowForgotModal(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: theme.colors.textSecondary,
                  cursor: 'pointer',
                  fontSize: theme.fonts.sizeSmall,
                  fontFamily: theme.fonts.body,
                  textDecoration: 'underline',
                }}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          )}

          {/* Toggle mode */}
          <div style={{
            textAlign: 'center',
            marginTop: '16px',
            color: theme.colors.textSecondary,
            fontSize: theme.fonts.sizeSmall,
          }}>
            {mode === 'login' ? (
              <span>
                ¿No tenés cuenta?{' '}
                <button
                  onClick={() => { setMode('register'); setError(''); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: theme.colors.primary,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontFamily: theme.fonts.body,
                  }}
                >
                  Registrate
                </button>
              </span>
            ) : (
              <span>
                ¿Ya tenés cuenta?{' '}
                <button
                  onClick={() => { setMode('login'); setError(''); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: theme.colors.primary,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontFamily: theme.fonts.body,
                  }}
                >
                  Iniciá sesión
                </button>
              </span>
            )}
          </div>
          </>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: isMobile ? '14px' : theme.spacing.lg,
          color: theme.colors.white,
          opacity: 0.8
        }}>
          <p style={{
            fontSize: isMobile ? '0.85rem' : theme.fonts.sizeSmall,
            margin: 0,
            fontFamily: theme.fonts.body,
            textShadow: '0 1px 2px rgba(0,0,0,0.3)'
          }}>
            © 2024 Futbolero - Organiza partidos, conecta jugadores
          </p>
        </div>
      </div>

      <ForgotPasswordModal
        isOpen={showForgotModal}
        initialEmail={email}
        onClose={() => setShowForgotModal(false)}
      />
    </div>
  );
};

export default AuthPage;
